// Shared resolution of the lobby builder's "Top sponsors" configuration.
//
// The admin configures top sponsors in two places, both storing the same
// shape (`{ size, elements: [{ id, sponsorId }] }`):
//
//   • the Top sponsors module → `element.options`
//   • the Banner module       → `element.options.topSponsorOptions`
//
// `elements` is an ORDERED list — the admin drags the rows into the order
// they want on the platform — and always carries one trailing placeholder
// row with no `sponsorId` (the empty dropdown in the builder), which must
// be ignored. `size` is the percentage behind the builder's "Size" slider
// (60–100); the platform turns it into a scale factor applied to the tile
// geometry, mirroring what the `tappin-top-sponsors` web component does
// with its `--card-size` custom property.

const MIN_SIZE = 60;
const MAX_SIZE = 100;
const DEFAULT_SCALE = 1;

// The `{ size, elements }` block for a lobby element's options. Accepts
// either the Top sponsors module options or a Banner's options (whose
// block lives under `topSponsorOptions`). Tolerant of PascalCase, which
// some API versions still emit.
export function topSponsorOptionsOf(options) {
  if (!options) return {};
  return options.topSponsorOptions || options.TopSponsorOptions || options;
}

// The admin's selected sponsor ids, in the order they arranged them.
// Placeholder rows (no `sponsorId`) and duplicates are dropped.
export function topSponsorIdsOf(options) {
  const block = topSponsorOptionsOf(options);
  const elements = block.elements || block.Elements;
  if (!Array.isArray(elements)) return [];

  const seen = new Set();
  const ids = [];
  elements.forEach((element) => {
    if (!element) return;
    const raw =
      element.sponsorId != null ? element.sponsorId : element.SponsorId;
    if (raw == null) return;
    const id = String(raw).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

// Admin "Size" slider → scale factor (0.6 – 1). Falls back to 1 for
// events saved before the slider existed, or with a malformed value.
export function topSponsorScale(size) {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_SCALE;
  return Math.min(Math.max(value, MIN_SIZE), MAX_SIZE) / 100;
}

// Resolve the admin's ordered selection against the sponsor list from the
// store. Preserves the admin's order and drops ids that no longer resolve
// (e.g. a sponsor deleted after the lobby page was saved).
export function resolveTopSponsors(options, sponsors) {
  const ids = topSponsorIdsOf(options);
  if (!ids.length) return [];
  if (!Array.isArray(sponsors) || !sponsors.length) return [];

  const byId = new Map();
  sponsors.forEach((sponsor) => {
    if (!sponsor) return;
    const raw = sponsor.id != null ? sponsor.id : sponsor.Id;
    const key = raw != null ? String(raw) : '';
    if (key && !byId.has(key)) byId.set(key, sponsor);
  });

  return ids.map((id) => byId.get(id)).filter(Boolean);
}

export default {
  topSponsorOptionsOf,
  topSponsorIdsOf,
  topSponsorScale,
  resolveTopSponsors
};
